import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '../../gql';
import React from 'react';
import { compact } from 'lodash';

import { withGraphql } from '../../with_graphql';
import { EVERYWHERE_CODE } from '../../constants';
import ListingsCollection from '../../components/listings_collection';
import { MParticleEventName } from '../../elog/mparticle_tracker';
import SanitizedRender from '../../components/sanitized_render';
import { IDynamicComponentProps } from '../dynamic_component_props';
import { withRouter, WithRouterProps } from 'react-router';
import {
  Commons_Cms_ListingsCollectionQuery,
  reverb_search_ListingsSearchRequest_PriceValue,
  reverb_search_ListingsSearchRequest_Sort,
  reverb_search_ListingsSearchRequest as ListingsSearchRequest,
} from '../../gql/graphql';
import {
  withUserContext,
  IUser,
  IUserContext,
} from '../../components/user_context_provider';
import { useViewTracking } from '../../use_tracking';

import { MParticleEvent } from '../../elog/mparticle_types';

import { userContextIsTrackable } from '../../elog/mparticle';
import {
  localizeListingsRowsFilters,
  showLocalizedListingContent,
} from '../../localize_listing_rows_utils';
import { OverflowingRowAction } from '../../components/overflowing_row_action';
import classNames from 'classnames';
import { BumpDSACompliancePopover } from '../../components/bump_dsa_compliance_popover';
import { MosaicPatternProps, ColSpanProps } from '../../components/mosaic_listings_collection';

const COMPONENT_NAME = 'CMSListingsCollection';

export interface ListingFields {
  id: string;
}

export enum ADDITIONAL_SORTS {
  BUMP_BOOST = 'BUMP_BOOST',
  RANDOM = 'RANDOM',
}

export type CMSListingsCollectionSorts = reverb_search_ListingsSearchRequest_Sort | ADDITIONAL_SORTS;

export interface ExternalProps extends IDynamicComponentProps {
  acceptsGiftCards?: boolean;
  acceptsOffers?: boolean;
  acceptsPaymentPlans?: boolean;
  brandUuids?: string[];
  brandSlugs?: string[];
  categoryUuids?: string[];
  componentName?: string;
  conditionSlugs?: string[];
  countryOfOrigin?: string;
  cspId?: string;
  ctaTargetHref?: string;
  ctaText?: string;
  curatedSetId?: string;
  currency?: string;
  displayAddToCart?: boolean;
  displayAsGrid?: boolean;
  freeExpeditedShipping?: boolean;
  freeShipping?: boolean;
  handmade?: boolean;
  holidaySale?: boolean;
  itemRegion?: string;
  jumplinkSlug?: string;
  largeTiles?: boolean;
  ledeHtml?: string;
  listings?: ListingFields[];
  maxCount?: number;
  minCount?: number;
  mParticleView?: MParticleEvent;
  onSale?: boolean;
  preferredSeller?: boolean;
  priceMax?: string;
  priceMin?: string;
  productType?: string;
  query?: string;
  saleCuratedSetId?: string;
  saleSlug?: string;
  shopSlugs?: string[];
  sort?: CMSListingsCollectionSorts;
  boostByBumpRate?: boolean;
  subCategory?: string;
  subtitle?: string | React.ReactNode;
  title?: string;
  titleHtml?: string;
  titleTooltip?: React.ReactNode;
  trackingName?: string;
  user?: IUser;
  userShippingRegion?: string;
  yearMax?: string;
  yearMin?: string;
  adKeywords?: string[];
  traits?: string[];
  dealsOrWellPriced?: boolean;
  priceValueV2?: reverb_search_ListingsSearchRequest_PriceValue;
  shipsToMe?: boolean;
  shouldDisplayAsLanding?: boolean;
  similarListingIds?: string[];
  withSidebar?: boolean;
  localPickup?: boolean;
  grayBackground?: boolean;
  indentedMosaic?: boolean;
  excludeShopIds?: string[];
  displayMobileMosaic?: boolean;
  handleCreateWatchResult?: () => void;
  mosaicListingRow?: boolean;
  mosaicPattern?: MosaicPatternProps;
  colCount?: ColSpanProps;
  includeItemListMetadata?: boolean;
  seed?: number;
  certifiedPreOwned?: boolean;
  sortPromotedSimilarListingsByBestMatch?: boolean;
}

export type IProps = ExternalProps & IUserContext;

export function getItemRegionFilters(ownProps): ListingsSearchRequest {
  if (ownProps.itemRegion) {
    return { itemRegion: ownProps.itemRegion };
  }

  /* If an item region is not specifically set, then default to everywhere.
  For most situations, this is a no-op. But if a sticky seller location exists for the user,
  we cancel it out to display more listings. It also prevents confusion
  since an implicit sticky seller location filter on CMS rows is surprising. */
  return { itemRegion: EVERYWHERE_CODE, ...localizeListingsRowsFilters(ownProps.user) };
}

export function getSort(ownProps) {
  if (ownProps.sort === ADDITIONAL_SORTS.BUMP_BOOST) {
    return reverb_search_ListingsSearchRequest_Sort.NONE;
  }
  return ownProps.sort;
}

function buildComponentName(componentName, sort) {
  if (sort === ADDITIONAL_SORTS.BUMP_BOOST) {
    return `Bumped${componentName}`;
  }

  return componentName;
}

function getSubtitle(ownProps) {
  if (ownProps.sort === ADDITIONAL_SORTS.BUMP_BOOST) {
    return <BumpDSACompliancePopover />;
  }
  return ownProps.subtitle;
}

function PromotedListingsCollection({
  callToAction,
  mParticleView,
  componentName,
  displayAsGrid,
  largeTiles,
  listings,
  loading,
  maxCount,
  title,
  titleTooltip,
  titleString,
  jumplinkSlug,
  ledeHtml,
  ad,
  showFlags,
  shouldDisplayAsLanding,
  withSidebar,
  handleCreateWatchResult,
  indentedMosaic,
  displayMobileMosaic,
  mosaicListingRow,
  mosaicPattern,
  colCount,
  includeItemListMetadata,
  cmsComponentId,
  displayAddToCart,
}) {

  return (
    <ListingsCollection
      callToAction={callToAction}
      mParticleView={mParticleView}
      componentName={componentName}
      displayAsGrid={displayAsGrid}
      largeTiles={largeTiles}
      listings={listings}
      loading={loading}
      maxCount={maxCount}
      title={title}
      titleTooltip={titleTooltip}
      titleString={titleString}
      jumplinkSlug={jumplinkSlug}
      ledeHtml={ledeHtml}
      ad={ad}
      showFlags={showFlags}
      shouldDisplayAsLanding={shouldDisplayAsLanding}
      withSidebar={withSidebar}
      handleCreateWatchResult={handleCreateWatchResult}
      indentedMosaic={indentedMosaic}
      displayMobileMosaic={displayMobileMosaic}
      mosaicListingRow={mosaicListingRow}
      mosaicPattern={mosaicPattern}
      colCount={colCount}
      includeItemListMetadata={includeItemListMetadata}
      cmsComponentId={cmsComponentId}
      displayAddToCart={displayAddToCart}
    />
  );
}

export function CMSListingsCollection(props: ChildProps<IProps & WithRouterProps, Commons_Cms_ListingsCollectionQuery>) {
  const minCount = props.minCount || 1;
  const componentName = buildComponentName(props.trackingName || props.componentName || COMPONENT_NAME, props.sort);
  const listings = props.data?.listingsSearch?.listings || [];
  const isPromotedListing = props.sort === ADDITIONAL_SORTS.BUMP_BOOST;

  const impressionData = {
    eventName: MParticleEventName.ListingImpression,
    listingIds: listings.map(l => l.id),
    listings,
    componentName,
  };

  useViewTracking(impressionData, props.data?.variables?.bumpedOnly && !props.data?.loading);
  const shouldNotRender = !props.data?.loading && listings.length < minCount;

  if (shouldNotRender) {
    return null;
  }

  const classes = classNames(
    { 'bg-offwhite bg--lighten ptb-8 mtb-6': props.grayBackground },
  );

  function callToAction() {
    return (
      <OverflowingRowAction
        href={props.ctaTargetHref}
        text={props.ctaText}
      />
    );
  }

  function renderTitle() {
    if (!props.titleHtml && !props.title) return null;
    return (
      <SanitizedRender
        html={props.titleHtml || props.title}
      />
    );
  }

  function getListingCollection() {
    if (isPromotedListing) {
      return (
        <PromotedListingsCollection
          callToAction={!!props.ctaTargetHref && callToAction()}
          mParticleView={props.mParticleView}
          componentName={componentName}
          displayAsGrid={props.displayAsGrid}
          largeTiles={props.largeTiles}
          listings={listings}
          loading={props.data.loading}
          maxCount={props.maxCount}
          title={renderTitle()}
          titleTooltip={props.titleTooltip}
          titleString={props.title}
          jumplinkSlug={props.jumplinkSlug}
          ledeHtml={props.ledeHtml}
          ad={props.data.adServe?.ad}
          showFlags={showLocalizedListingContent(props.user)}
          shouldDisplayAsLanding={props.shouldDisplayAsLanding}
          withSidebar={props.withSidebar}
          handleCreateWatchResult={props.handleCreateWatchResult}
          indentedMosaic={props.indentedMosaic}
          displayMobileMosaic={props.displayMobileMosaic}
          mosaicListingRow={props.mosaicListingRow}
          mosaicPattern={props.mosaicPattern}
          colCount={props.colCount}
          includeItemListMetadata={props.includeItemListMetadata}
          cmsComponentId={props.componentId}
          displayAddToCart={props.displayAddToCart}
        />
      );
    }
    return (
      <ListingsCollection
        callToAction={!!props.ctaTargetHref && callToAction()}
        mParticleView={props.mParticleView}
        componentName={componentName}
        displayAsGrid={props.displayAsGrid}
        largeTiles={props.largeTiles}
        listings={listings}
        loading={props.data.loading}
        maxCount={props.maxCount}
        title={renderTitle()}
        titleTooltip={props.titleTooltip}
        titleString={props.title}
        jumplinkSlug={props.jumplinkSlug}
        subtitle={getSubtitle(props)}
        ledeHtml={props.ledeHtml}
        ad={props.data.adServe?.ad}
        showFlags={showLocalizedListingContent(props.user)}
        shouldDisplayAsLanding={props.shouldDisplayAsLanding}
        withSidebar={props.withSidebar}
        handleCreateWatchResult={props.handleCreateWatchResult}
        indentedMosaic={props.indentedMosaic}
        displayMobileMosaic={props.displayMobileMosaic}
        mosaicListingRow={props.mosaicListingRow}
        mosaicPattern={props.mosaicPattern}
        colCount={props.colCount}
        includeItemListMetadata={props.includeItemListMetadata}
        cmsComponentId={props.componentId}
        displayAddToCart={props.displayAddToCart}
      />
    );
  }

  return (
    props.grayBackground ? (
      <div className={classes} >
        {getListingCollection()}
      </div>
    ) : (
      getListingCollection()
    )
  );
}

export const CMS_LISTINGS_COLLECTION_QUERY = gql(`
  query Commons_Cms_ListingsCollection(
    $acceptsGiftCards: Boolean
    $acceptsOffers: Boolean
    $acceptsPaymentPlans: Boolean
    $brandUuids: [String]
    $brandSlugs: [String]
    $bumpedOnly: Boolean!
    $boostByBumpRate: Boolean!
    $categorySlugs: [String]
    $categoryUuids: [String]
    $conditionSlugs: [String]
    $countryOfOrigin: [String]
    $cspId: String
    $curatedSetId: String
    $currency: String
    $freeExpeditedShipping: Boolean
    $freeShipping: Boolean
    $handmade: Boolean
    $holidaySale: Boolean
    $itemRegion: String
    $itemRegionRelation: reverb_search_ListingItemRegionRelation
    $limit: Int
    $listingIds: [String]
    $onSale: Boolean
    $preferredSeller: Boolean
    $priceMax: String
    $priceMin: String
    $query: String
    $saleCuratedSetId: String
    $saleSlugs: [String]
    $shippingRegionCodes: [String]
    $shopSlugs: [String]
    $sort: reverb_search_ListingsSearchRequest_Sort
    $yearMax: String
    $yearMin: String
    $adKeywords: [String]
    $hasAdKeywords: Boolean!
    $traitSlugs: [String]
    $dealsOrWellPriced: Boolean
    $priceValueV2: reverb_search_ListingsSearchRequest_PriceValue
    $shipsToMe: Boolean
    $shouldSkipTracking: Boolean!
    $similarListingIds: [String]
    $localPickup: Boolean
    $excludeShopIds: [String]
    $randomSeed: Int
    $certifiedPreOwned: Boolean
  ) {
    listingsSearch(
      input: {
        acceptsGiftCards: $acceptsGiftCards
        acceptsOffers: $acceptsOffers
        acceptsPaymentPlans: $acceptsPaymentPlans
        brandUuids: $brandUuids
        brandSlugs: $brandSlugs
        bumpedOnly: $bumpedOnly
        boostByBumpRate: $boostByBumpRate
        categorySlugs: $categorySlugs
        categoryUuids: $categoryUuids
        conditionSlugs: $conditionSlugs
        countryOfOrigin: $countryOfOrigin
        cspId: $cspId
        curatedSetId: $curatedSetId
        currency: $currency
        freeExpeditedShipping: $freeExpeditedShipping
        freeShipping: $freeShipping
        handmade: $handmade
        holidaySale: $holidaySale
        ids: $listingIds
        itemRegion: $itemRegion
        itemRegionRelation: $itemRegionRelation
        limit: $limit
        onSale: $onSale
        preferredSeller: $preferredSeller
        priceMax: $priceMax
        priceMin: $priceMin
        query: $query
        saleCuratedSetId: $saleCuratedSetId
        saleSlugs: $saleSlugs
        shippingRegionCodes: $shippingRegionCodes
        shopSlugs: $shopSlugs
        sort: $sort
        traitSlugs: $traitSlugs
        yearMax: $yearMax
        yearMin: $yearMin
        statuses: ["live"],
        dealsOrWellPriced: $dealsOrWellPriced
        priceValueV2: $priceValueV2
        shipsToMe: $shipsToMe
        similarListingIds: $similarListingIds
        localPickup: $localPickup
        excludeShopIds: $excludeShopIds
        randomSeed: $randomSeed
        certifiedPreOwned: $certifiedPreOwned
      }
    ) {
      listings {
        _id
        ... ListingsCollection
        ... mParticleListingFields @skip(if: $shouldSkipTracking)
        ... BumpKey @include(if: $bumpedOnly)
      }
    }
    adServe(input: {
      targeting: {
        keywords: $adKeywords
      }
    }) @include (if: $hasAdKeywords) {
      ad {
        ...AdData
        imageAdCreative {
          ...ImageAdCreativeData
        }
      }
    }
  }
`);

export function buildCmsListingsCollectionVars(ownProps: IProps & WithRouterProps) {
  return {
    ssr: ownProps.ssr,
    variables: {
      acceptsGiftCards: ownProps.acceptsGiftCards,
      acceptsOffers: ownProps.acceptsOffers,
      acceptsPaymentPlans: ownProps.acceptsPaymentPlans,
      brandUuids: ownProps.brandUuids,
      brandSlugs: ownProps.brandSlugs,
      bumpedOnly: ownProps.sort === ADDITIONAL_SORTS.BUMP_BOOST,
      // Turn off boostByBumpRate when in experiment.
      // If experiment is rolled forward, refactor CMSListingsCollection to _not_ use BUMP_BOOST
      // as the flag for a promoted listings row.
      boostByBumpRate: ownProps.sort === ADDITIONAL_SORTS.BUMP_BOOST && !ownProps.sortPromotedSimilarListingsByBestMatch,
      categorySlugs: compact([
        ownProps.productType,
        ownProps.subCategory,
      ]),
      categoryUuids: ownProps.categoryUuids,
      conditionSlugs: ownProps.conditionSlugs,
      cspId: ownProps.cspId,
      countryOfOrigin: compact([ownProps.countryOfOrigin]),
      curatedSetId: ownProps.curatedSetId,
      currency: ownProps.currency,
      freeExpeditedShipping: ownProps.freeExpeditedShipping,
      freeShipping: ownProps.freeShipping,
      handmade: ownProps.handmade,
      holidaySale: ownProps.holidaySale,
      ...getItemRegionFilters(ownProps),
      limit: ownProps.maxCount,
      listingIds: (ownProps.listings || []).map(l => l.id),
      onSale: ownProps.onSale,
      preferredSeller: ownProps.preferredSeller,
      priceMax: ownProps.priceMax,
      priceMin: ownProps.priceMin,
      query: ownProps.query,
      saleCuratedSetId: ownProps.saleCuratedSetId,
      saleSlugs: ownProps.saleSlug && [ownProps.saleSlug],
      shippingRegionCodes: [ownProps.userShippingRegion || ownProps.user?.shippingRegionCode],
      shopSlugs: ownProps.shopSlugs,
      sort: getSort(ownProps),
      yearMax: ownProps.yearMax,
      yearMin: ownProps.yearMin,
      adKeywords: ownProps.adKeywords,
      hasAdKeywords: !!(ownProps.adKeywords && ownProps.adKeywords.length > 0),
      traitSlugs: ownProps.traits,
      dealsOrWellPriced: ownProps.dealsOrWellPriced,
      priceValueV2: ownProps.priceValueV2,
      shipsToMe: ownProps.shipsToMe,
      shouldSkipTracking: !userContextIsTrackable(ownProps.user),
      similarListingIds: ownProps.similarListingIds,
      localPickup: ownProps.localPickup,
      excludeShopIds: ownProps.excludeShopIds,
      randomSeed: ownProps.seed,
      certifiedPreOwned: ownProps.certifiedPreOwned,
    },
  };
}

const query = withGraphql<IProps & WithRouterProps, Commons_Cms_ListingsCollectionQuery>(
  CMS_LISTINGS_COLLECTION_QUERY,
  {
    options: buildCmsListingsCollectionVars,
  },
);

export default withUserContext<IProps>(withRouter(query(CMSListingsCollection)));
